/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import { useCallback, useRef, useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { getCondominiumPrefill } from '@services/requests/housien-services/PropertyFieldValuesService';
import { planCondominiumPrefill } from './condominium-prefill';
import type { PropertyFormValues } from './schema';

/** What the notice under the condominium field reports. */
export interface CondominiumPrefillNotice {
  condominium: string;
  matched: number;
  groups: string[];
}

/**
 * Applies the condominium prefill when a condominium is picked from the
 * suggestions: asks the server what the agency's other listings in it share
 * and fills only the form fields that are still empty.
 *
 * @param form - The property drawer form.
 * @param agencyId - The agency whose listings vote.
 * @param propertyId - The property being edited, left out of the vote.
 * @returns The notice to show (null when nothing was filled) and the pick handler.
 */
export function useCondominiumPrefill(
  form: UseFormReturn<PropertyFormValues>,
  agencyId: string,
  propertyId?: string,
) {
  const [notice, setNotice] = useState<CondominiumPrefillNotice | null>(null);
  const requestId = useRef(0);

  const onPick = useCallback((name: string) => {
    const id = ++requestId.current;
    setNotice(null);
    getCondominiumPrefill(agencyId, name, propertyId)
      .then((res) => {
        if (id !== requestId.current) return;
        const plan = planCondominiumPrefill(form.getValues(), res.values);
        Object.entries(plan.patch).forEach(([key, value]) => {
          form.setValue(key as keyof PropertyFormValues, value as never, { shouldDirty: true });
        });
        if (plan.groups.length > 0) {
          setNotice({ condominium: res.condominium || name, matched: res.matched, groups: plan.groups });
        }
      })
      .catch(() => {
        // A failed suggestion leaves the form as the user left it.
      });
  }, [form, agencyId, propertyId]);

  const dismiss = useCallback(() => setNotice(null), []);

  return { notice, onPick, dismiss };
}
